import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import { formatJalaliDate, formatJalaliDateTime } from "../shop/utils/dateFormat";

// 🔥 ULTIMATE FIX: Get product name from ANY data structure
const getProductName = (stock) => {
  // Check ALL possible locations for product name
  
  // 1. Direct stock entry - product might be at root level
  if (stock?.product?.name) {
    return stock.product.name;
  }
  
  // 2. If product is a string directly
  if (typeof stock?.product === 'string') {
    return stock.product;
  }
  
  // 3. Product name at root level
  if (stock?.product_name) {
    return stock.product_name;
  }
  
  // 4. From purchase flow
  if (stock?.purchase_item_details?.product?.name) {
    return stock.purchase_item_details.product.name;
  }
  
  // 5. From purchase item details with item
  if (stock?.purchase_item_details?.item?.product?.name) {
    return stock.purchase_item_details.item.product.name;
  }
  
  // 6. If product is in data object
  if (stock?.data?.product?.name) {
    return stock.data.product.name;
  }
  
  // 7. If product is in item object
  if (stock?.item?.product?.name) {
    return stock.item.product.name;
  }
  
  // 8. If product details is an array or object with name
  if (stock?.product_details?.name) {
    return stock.product_details.name;
  }
  
  // 9. If product has title instead of name
  if (stock?.product?.title) {
    return stock.product.title;
  }
  
  // 10. Try to find any field that might contain product name
  const possibleFields = ['name', 'title', 'product_name', 'product_title', 'item_name'];
  for (const field of possibleFields) {
    if (stock?.[field]) {
      return stock[field];
    }
    if (stock?.product?.[field]) {
      return stock.product[field];
    }
    if (stock?.purchase_item_details?.product?.[field]) {
      return stock.purchase_item_details.product[field];
    }
  }
  
  // If we have product ID but no name, show the ID
  if (stock?.product_id) {
    return `Product #${stock.product_id}`;
  }
  
  return '--';
};

// 🔥 ULTIMATE FIX: Get supplier name from ANY data structure
const getSupplierName = (stock) => {
  // Direct stock entry
  if (stock?.supplier?.name) {
    return stock.supplier.name;
  }
  
  if (typeof stock?.supplier === 'string') {
    return stock.supplier;
  }
  
  if (stock?.supplier_name) {
    return stock.supplier_name;
  }
  
  // From purchase flow
  if (stock?.purchase_item_details?.purchase?.supplier?.name) {
    return stock.purchase_item_details.purchase.supplier.name;
  }
  
  if (stock?.purchase_item_details?.supplier?.name) {
    return stock.purchase_item_details.supplier.name;
  }
  
  // From data object
  if (stock?.data?.supplier?.name) {
    return stock.data.supplier.name;
  }
  
  // Try to find any field with supplier
  if (stock?.supplier_details?.name) {
    return stock.supplier_details.name;
  }
  
  return '--';
};

// 🔥 ULTIMATE FIX: Get unit from ANY data structure
const getUnitTitle = (stock) => {
  if (stock?.unit?.title) {
    return stock.unit.title;
  }
  if (typeof stock?.unit === 'string') {
    return stock.unit;
  }
  if (stock?.unit_name) {
    return stock.unit_name;
  }
  if (stock?.unit_details?.title) {
    return stock.unit_details.title;
  }
  if (stock?.purchase_item_details?.unit?.title) {
    return stock.purchase_item_details.unit.title;
  }
  return '--';
};

// 🔥 ULTIMATE FIX: Get currency from ANY data structure
const getCurrencyTitle = (stock) => {
  if (stock?.currency?.title) {
    return stock.currency.title;
  }
  if (typeof stock?.currency === 'string') {
    return stock.currency;
  }
  if (stock?.currency_name) {
    return stock.currency_name;
  }
  if (stock?.currency_details?.title) {
    return stock.currency_details.title;
  }
  if (stock?.purchase_item_details?.currency?.title) {
    return stock.purchase_item_details.currency.title;
  }
  return '--';
};

// 🔥 ULTIMATE FIX: Get branch from ANY data structure
const getBranchName = (stock) => {
  if (stock?.branch?.name) {
    return stock.branch.name;
  }
  if (typeof stock?.branch === 'string') {
    return stock.branch;
  }
  if (stock?.branch_name) {
    return stock.branch_name;
  }
  if (stock?.shop?.name) {
    return stock.shop.name;
  }
  if (stock?.shop_details?.name) {
    return stock.shop_details.name;
  }
  if (stock?.purchase_item_details?.shop?.name) {
    return stock.purchase_item_details.shop.name;
  }
  return '--';
};

// 🔥 FIXED: Get sale price from ANY data structure
const getSalePrice = (stock) => {
  if (stock?.sale_price) {
    return stock.sale_price;
  }
  if (stock?.retail_price) {
    return stock.retail_price;
  }
  if (stock?.price) {
    return stock.price;
  }
  if (stock?.purchase_item_details?.sale_price) {
    return stock.purchase_item_details.sale_price;
  }
  if (stock?.purchase_item_details?.price) {
    return stock.purchase_item_details.price;
  }
  if (stock?.wholesale_price) {
    return stock.wholesale_price;
  }
  return '0.00';
};

// 🔥 FIXED: Get quantity from ANY data structure
const getQuantity = (stock) => {
  if (stock?.quantity) {
    return stock.quantity;
  }
  if (stock?.available_qty) {
    return stock.available_qty;
  }
  if (stock?.qty) {
    return stock.qty;
  }
  if (stock?.available_quantity) {
    return stock.available_quantity;
  }
  if (stock?.stock_quantity) {
    return stock.stock_quantity;
  }
  if (stock?.purchase_item_details?.quantity) {
    return stock.purchase_item_details.quantity;
  }
  return '0';
};

// Export stock data to PDF
export const exportStockToPDF = (stocks, searchTerm = '', productFilter = '') => {
  const doc = new jsPDF('landscape', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header
  doc.setFillColor(26, 35, 126);
  doc.rect(0, 0, pageWidth, 35, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('📦 Stock Inventory Report', 15, 20);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated on: ${formatJalaliDate()}`, 15, 28);
  doc.text(`Total Items: ${stocks.length}`, pageWidth - 15, 28, { align: 'right' });

  // Prepare table data
  const tableData = stocks.map((stock, index) => {
    // 🔥 DEBUG: Uncomment to see the actual stock data structure
    // console.log(`Stock ${index + 1}:`, JSON.stringify(stock, null, 2));
    
    return [
      index + 1,
      getProductName(stock),
      getSupplierName(stock),
      getSalePrice(stock),
      getQuantity(stock),
      getUnitTitle(stock),
      getCurrencyTitle(stock),
      getBranchName(stock)
    ];
  });

  autoTable(doc, {
    head: [['#', 'Product', 'Supplier', 'Sale Price', 'Quantity', 'Unit', 'Currency', 'Branch']],
    body: tableData,
    startY: 45,
    theme: 'striped',
    headStyles: {
      fillColor: [26, 35, 126],
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 3
    },
    columnStyles: {
      0: { cellWidth: 15 },
      1: { cellWidth: 45 },
      2: { cellWidth: 35 },
      3: { cellWidth: 25 },
      4: { cellWidth: 20 },
      5: { cellWidth: 20 },
      6: { cellWidth: 25 },
      7: { cellWidth: 35 }
    },
    margin: { top: 45, left: 15, right: 15 }
  });

  // Footer
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(108, 117, 125);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - 15, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
  }

  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
  doc.save(`Stock_Report_${timestamp}.pdf`);
};

// Export stock data to Excel
export const exportStockToExcel = (stocks) => {
  const worksheetData = stocks.map((stock, index) => ({
    '#': index + 1,
    'Product Name': getProductName(stock),
    'Supplier': getSupplierName(stock),
    'Sale Price': getSalePrice(stock),
    'Available Quantity': getQuantity(stock),
    'Unit': getUnitTitle(stock),
    'Currency': getCurrencyTitle(stock),
    'Branch': getBranchName(stock),
    'Product ID': stock?.product_id || stock?.product?.id || stock?.purchase_item_details?.product?.id || '',
    'Stock ID': stock?.id || ''
  }));

  const worksheet = XLSX.utils.json_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  
  const colWidths = [
    { wch: 5 },   // #
    { wch: 30 },  // Product Name
    { wch: 25 },  // Supplier
    { wch: 12 },  // Sale Price
    { wch: 15 },  // Available Quantity
    { wch: 10 },  // Unit
    { wch: 12 },  // Currency
    { wch: 20 },  // Branch
    { wch: 12 },  // Product ID
    { wch: 10 }   // Stock ID
  ];
  worksheet['!cols'] = colWidths;

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock Report');
  
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
  XLSX.writeFile(workbook, `Stock_Report_${timestamp}.xlsx`);
};

// Export stock data to CSV
export const exportStockToCSV = (stocks) => {
  const csvData = stocks.map((stock, index) => ({
    '#': index + 1,
    'Product Name': getProductName(stock),
    'Supplier': getSupplierName(stock),
    'Sale Price': getSalePrice(stock),
    'Available Quantity': getQuantity(stock),
    'Unit': getUnitTitle(stock),
    'Currency': getCurrencyTitle(stock),
    'Branch': getBranchName(stock)
  }));

  const worksheet = XLSX.utils.json_to_sheet(csvData);
  const csv = XLSX.utils.sheet_to_csv(worksheet);
  
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
  saveAs(blob, `Stock_Report_${timestamp}.csv`);
};

// Print stock list
export const printStockList = (stocks) => {
  const printContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Stock Inventory Report</title>
        <meta charset="utf-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            font-size: 12px;
            line-height: 1.4;
            color: #2c3e50;
            background: white;
            padding: 20px;
          }
          .header { 
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            text-align: center;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 20px;
          }
          .header h1 { 
            font-size: 24px;
            font-weight: 700;
            margin-bottom: 8px;
          }
          .header p { 
            font-size: 14px;
            opacity: 0.9;
          }
          .info-bar {
            background: #f8f9fa;
            padding: 15px;
            border-radius: 6px;
            margin-bottom: 20px;
            display: flex;
            justify-content: space-between;
            border-left: 4px solid #667eea;
            flex-wrap: wrap;
            gap: 10px;
          }
          .info-item {
            padding: 5px 10px;
          }
          table { 
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          th { 
            background: linear-gradient(90deg, #667eea, #764ba2);
            color: white;
            font-weight: 600;
            padding: 12px 8px;
            text-align: left;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          td { 
            padding: 10px 8px;
            border-bottom: 1px solid #e9ecef;
            font-size: 11px;
            vertical-align: middle;
          }
          tr:nth-child(even) { 
            background: #f8f9fa;
          }
          tr:hover { 
            background: #e3f2fd;
          }
          .product-name {
            font-weight: 600;
            color: #2c3e50;
          }
          .footer { 
            margin-top: 30px;
            padding: 15px;
            background: #f8f9fa;
            border-radius: 6px;
            text-align: center;
            color: #6c757d;
            font-size: 10px;
          }
          .no-data {
            text-align: center;
            color: #999;
            padding: 40px;
            font-size: 14px;
          }
          @media print {
            body { padding: 10px; }
            .header { margin-bottom: 15px; }
            .info-bar { margin-bottom: 15px; }
          }
          @page {
            margin: 0.5in;
            size: A4 landscape;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>📦 Stock Inventory Report</h1>
          <p>Generated on ${formatJalaliDate()}</p>
        </div>
        
        <div class="info-bar">
          <div class="info-item"><strong>Total Items:</strong> ${stocks.length}</div>
          <div class="info-item"><strong>Report Type:</strong> Complete Stock List</div>
          <div class="info-item"><strong>Status:</strong> Current Inventory</div>
        </div>

        ${stocks.length > 0 ? `
        <table>
          <thead>
            <tr>
              <th style="width: 5%">#</th>
              <th style="width: 25%">Product Name</th>
              <th style="width: 15%">Supplier</th>
              <th style="width: 10%">Sale Price</th>
              <th style="width: 10%">Quantity</th>
              <th style="width: 8%">Unit</th>
              <th style="width: 8%">Currency</th>
              <th style="width: 15%">Branch</th>
            </tr>
          </thead>
          <tbody>
            ${stocks.map((stock, index) => `
              <tr>
                <td>${index + 1}</td>
                <td><span class="product-name">${getProductName(stock)}</span></td>
                <td>${getSupplierName(stock)}</td>
                <td style="text-align: right; font-weight: 600;">${getSalePrice(stock)}</td>
                <td style="text-align: center; font-weight: 600;">${getQuantity(stock)}</td>
                <td>${getUnitTitle(stock)}</td>
                <td>${getCurrencyTitle(stock)}</td>
                <td>${getBranchName(stock)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        ` : `
        <div class="no-data">
          <p>No stock items found</p>
        </div>
        `}

        <div class="footer">
          <p><strong>📦 Stock Inventory Report</strong> • Generated by Inventory Management System</p>
          <p>Report generated on ${formatJalaliDateTime()}</p>
        </div>
      </body>
    </html>
  `;

  const printWindow = window.open('', '_blank', 'width=1200,height=800,scrollbars=yes');
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  } else {
    alert('Please allow popups to print the report');
  }

  return printWindow;
};